/**
 * API Utility Functions
 * 
 * ⚠️ CRITICAL: All API requests MUST go through API Gateway (Kong on port 30000)
 * DO NOT use direct service URLs (ports 30001-30006)
 * 
 * This file provides a centralized function to get the backend URL with
 * validation to prevent bypassing the API Gateway.
 */

/**
 * Get the backend URL (API Gateway) for all API requests
 * 
 * This function enforces that all requests go through the Kong API Gateway.
 * It validates the REACT_APP_BACKEND_URL environment variable and prevents
 * direct service access (ports 30001-30006).
 * 
 * @returns The backend URL (should be API Gateway on port 30000)
 * @throws Error if REACT_APP_BACKEND_URL is not set or if direct service access is detected
 */
export const getBackendUrl = (): string => {
  if (!process.env.REACT_APP_BACKEND_URL) {
    throw new Error(
      'REACT_APP_BACKEND_URL environment variable is not set. ' +
      'Please configure it in your .env file to point to the API Gateway (port 30000).'
    );
  }
  
  let backendUrl = process.env.REACT_APP_BACKEND_URL.trim();
  
  // ENFORCEMENT: Prevent direct service access (bypassing API Gateway)
  // Only allow gateway URL (port 30000) or relative URLs
  try {
    const urlObj = new URL(backendUrl);
    const port = urlObj.port || (urlObj.protocol === 'https:' ? '443' : '80');
    const directServicePorts = ['30001', '30002', '30003', '30004', '30005', '30006'];
    
    if (directServicePorts.includes(port)) {
      const errorMsg = `❌ SECURITY VIOLATION: Direct service access detected on port ${port}. ` +
        `All requests MUST go through API Gateway (port 30000). ` +
        `Update REACT_APP_BACKEND_URL in .env to use http://127.0.0.1:30000`;
      console.error(errorMsg);
      throw new Error(errorMsg);
    }
  } catch (e) {
    // If URL parsing fails, check string pattern for direct service ports
    if (/\b3000[1-6]\b/.test(backendUrl)) {
      const errorMsg = '❌ SECURITY VIOLATION: Direct service access detected. ' +
        'All requests MUST go through API Gateway (port 30000). ' +
        'Update REACT_APP_BACKEND_URL in .env to use http://127.0.0.1:30000';
      console.error(errorMsg);
      throw new Error(errorMsg);
    }
    // Re-throw if it's our validation error
    if (e instanceof Error && e.message.includes('SECURITY VIOLATION')) {
      throw e;
    }
    // If URL parsing failed for other reasons, continue with validation
  }
  
  // Force IPv4 by replacing localhost with 127.0.0.1 to avoid IPv6 connection issues
  // This fixes connection reset errors when Kong gateway is accessed via localhost
  try {
    const urlObj = new URL(backendUrl);
    if (urlObj.hostname === 'localhost' || urlObj.hostname === '::1') {
      urlObj.hostname = '127.0.0.1';
      backendUrl = urlObj.toString();
    }
  } catch (e) {
    // URL parsing failed, try string replacement as fallback
    backendUrl = backendUrl.replace(/localhost/g, '127.0.0.1').replace(/::1/g, '127.0.0.1');
  }
  
  // If page is loaded over HTTPS, ensure backend URL also uses HTTPS
  // This prevents mixed content errors
  const currentProtocol = window.location.protocol;
  if (currentProtocol === 'https:') {
    if (backendUrl.startsWith('http://')) {
      backendUrl = backendUrl.replace('http://', 'https://');
    }
  }
  
  // Check if backend is on the same domain - use relative URL to avoid mixed content
  try {
    const backendUrlObj = new URL(backendUrl);
    const currentHost = window.location.host;
    const currentPort = window.location.port;
    const backendHost = backendUrlObj.host;
    const backendPort = backendUrlObj.port || (backendUrlObj.protocol === 'https:' ? '443' : '80');
    const currentPortNormalized = currentPort || (currentProtocol === 'https:' ? '443' : '80');
    
    // If same domain and same port (or both using default ports), use relative URL
    // This ensures browser uses same protocol (HTTPS) automatically
    if (backendHost === currentHost && backendPort === currentPortNormalized) {
      // Return empty string for relative URLs (API calls will be like /api/asr)
      return '';
    }
  } catch (e) {
    // URL parsing failed, continue with existing logic
  }
  
  // Remove trailing slash to avoid double slashes when appending paths
  backendUrl = backendUrl.replace(/\/+$/, '');
  
  return backendUrl;
};

/**
 * Get the gateway URL (alias for getBackendUrl for clarity)
 * 
 * @returns The API Gateway URL
 */
export const getGatewayUrl = (): string => {
  return getBackendUrl();
};
